import api from './AxiosConfig';
import { TenantData, TenantApiResponse } from 'types/tenant';

class TenantService {
  private baseUrl = process.env.REACT_APP_API_URL || '';

  /**
   * @param domain
   * @returns
   */
  async getTenantByDomain(domain: string): Promise<TenantData | null> {
    try {
      const response = await api.get(`${this.baseUrl}/api/configuration/v1/contexts`);
      const apiData = response.data;
      if (!apiData) {
        return null;
      }
      const tenantData = this.mapApiResponseToTenantData(apiData, domain);
      sessionStorage.setItem(`tenant_${domain}`, JSON.stringify(tenantData));
      return tenantData;
    } catch (error: any) {
      // Qualquer erro (CORS, rede, status) lança para ser tratado pelo TenantContext
      throw error;
    }
  }

  /**
   * @param tenantId - ID do tenant
   * @returns Promise com dados do tenant
   */
  async getTenantById(tenantId: string): Promise<TenantData> {
    const cached = sessionStorage.getItem(`tenant_${tenantId}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (this.validateTenantData(parsed)) {
          return parsed;
        }
      } catch {}
    }

    try {
      const response = await api.get(`${this.baseUrl}/api/configuration/v1/contexts`);
      const apiData = response.data;
      const tenantData = this.mapApiResponseToTenantData(apiData, tenantId);

      sessionStorage.setItem(`tenant_${tenantId}`, JSON.stringify(tenantData));
      return tenantData;
    } catch (error: any) {
      const fallback = this.getDefaultTenant(tenantId);
      sessionStorage.setItem(`tenant_${tenantId}`, JSON.stringify(fallback));
      return fallback;
    }
  }

  /**
   * @param tenantId
   * @param data
   * @returns
   */
  async updateTenant(tenantId: string, data: Partial<TenantData>): Promise<TenantData> {
    try {
      const response = await api.put<TenantApiResponse>(`${this.baseUrl}/api/configuration/v1/contexts`, data);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao atualizar dados do tenant');
      }
      sessionStorage.setItem(`tenant_${tenantId}`, JSON.stringify(response.data.data));
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar configurações do cliente');
    }
  }

  /**
   * @returns
   */
  getCurrentDomain(): string {
    if (typeof window === 'undefined') return '';

    let domain = window.location.hostname;

    if (domain === 'localhost' || domain === '127.0.0.1') {
      const urlParams = new URLSearchParams(window.location.search);
      const tenantParam = urlParams.get('tenant');
      if (tenantParam) {
        domain = tenantParam;
      } else {
        domain = 'default';
      }
    }

    return domain;
  }

  /**
   * @param apiData
   * @param domain
   * @returns
   */
  private mapApiResponseToTenantData(apiData: any, domain: string): TenantData {
    return {
      id: apiData._id || 'api-tenant',
      name: apiData.app?.title || 'Sendio Chatbot',
      domain: domain,
      logo: apiData.branding?.logo || '',
      favicon: apiData.branding?.favicon || '/favicon.svg',
      primaryColor: apiData.branding?.primaryColor || '#0065cbff', // Valores padrão já que não vem da API
      secondaryColor: apiData.branding?.secondaryColor || '#dc004e',
      backgroundColor: '#ffffff',
      textColor: '#000000',
      companyName: apiData.app?.title || 'Sendio Chatbot',
      supportEmail: 'support@sendio.com', // Valores padrão
      supportPhone: undefined,
      address: undefined,
      customCss: undefined,
      features: {
        chat: apiData.app?.enabled ?? true,
        reports: true, // Valores padrão
        analytics: true,
        customizations: false,
        apiAccess: true
      },
      settings: {
        loginBackgroundImage: undefined,
        loginWelcomeMessage: apiData.app?.description || 'Bem-vindo ao Sendio Chatbot',
        sessionTimeout: 3600000, // 1 hora
        allowRegistration: false,
        requireEmailVerification: true,
        passwordPolicy: {
          minLength: 8,
          requireUppercase: true,
          requireLowercase: true,
          requireNumbers: true,
          requireSpecialChars: false
        }
      }
    };
  }

  /**
   * @param domain - Domínio solicitado
   * @returns Dados padrão do tenant
   */
  private getDefaultTenant(domain: string): TenantData {
    return {
      id: 'default',
      name: 'Sendio Chatbot',
      domain: domain,
      logo: '', // Logo vazio para usar LogoMain como padrão
      favicon: '/favicon.svg',
      primaryColor: '#0065cbff',
      secondaryColor: '#dc004e',
      backgroundColor: '#ffffff',
      textColor: '#000000',
      companyName: 'Sendio Chatbot',
      supportEmail: 'support@sendio.com',
      features: {
        chat: true,
        reports: true,
        analytics: true,
        customizations: false,
        apiAccess: true
      },
      settings: {
        loginWelcomeMessage: 'Bem-vindo ao Sendio Chatbot',
        sessionTimeout: 3600000, // 1 hora em ms
        allowRegistration: false,
        requireEmailVerification: true,
        passwordPolicy: {
          minLength: 8,
          requireUppercase: true,
          requireLowercase: true,
          requireNumbers: true,
          requireSpecialChars: false
        }
      }
    };
  }

  /**
   * @param tenant
   * @returns
   */
  validateTenantData(tenant: TenantData): boolean {
    return !!(tenant.id && tenant.name && tenant.domain);
  }

  /**
   * @param tenant
   */
  applyTenantStyles(tenant: TenantData): void {
    if (!tenant) return;

    const root = document.documentElement;

    if (tenant.primaryColor) {
      root.style.setProperty('--tenant-primary-color', tenant.primaryColor);
    }
    if (tenant.secondaryColor) {
      root.style.setProperty('--tenant-secondary-color', tenant.secondaryColor);
    }
    if (tenant.backgroundColor) {
      root.style.setProperty('--tenant-background-color', tenant.backgroundColor);
    }
    if (tenant.textColor) {
      root.style.setProperty('--tenant-text-color', tenant.textColor);
    }

    if (tenant.favicon) {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = tenant.favicon;
      }
    }

    if (tenant.companyName) {
      document.title = tenant.companyName;
    }

    if (tenant.customCss) {
      const styleElement = document.getElementById('tenant-custom-styles');
      if (styleElement) {
        styleElement.innerHTML = tenant.customCss;
      } else {
        const style = document.createElement('style');
        style.id = 'tenant-custom-styles';
        style.innerHTML = tenant.customCss;
        document.head.appendChild(style);
      }
    }
  }

  removeTenantStyles(): void {
    const root = document.documentElement;
    root.style.removeProperty('--tenant-primary-color');
    root.style.removeProperty('--tenant-secondary-color');
    root.style.removeProperty('--tenant-background-color');
    root.style.removeProperty('--tenant-text-color');

    const customStyles = document.getElementById('tenant-custom-styles');
    if (customStyles) {
      customStyles.remove();
    }
  }
}

export default new TenantService();
